<template>
  <ModalModule
    v-model="open"
    :title="
      $textDictionary['lightingAdvisor.accessories.title'] ??
      'Complete your lighting setup'
    "
    :close-icon-text="
      $textDictionary['productFinder.modal.common.closeIconText'] ?? 'Close'
    "
    :adaptive-width="true"
    :esc-to-close="true"
    :click-to-close="true"
    data-testid="lighting-advisor-accessory-modal"
    @abort="open = false"
  >
    <div class="lighting-advisor-accessories">
      <p class="lighting-advisor-accessories__intro">
        {{
          $textDictionary['lightingAdvisor.accessories.intro'] ??
          'Info: A complete image processing system usually includes several components. In addition to a camera, a lens, lighting, cables and other accessories are usually required.'
        }}
      </p>

      <div
        v-if="loading"
        class="action-bar__summary lighting-advisor-accessories__summary"
        data-testid="lighting-advisor-accessory-bundle-loading"
      >
        <SectionBox class="lighting-advisor-accessories__box">
          <div class="lighting-advisor-accessories__box-content">
            <div
              v-for="index in 3"
              :key="index"
              class="action-bar__summary-product"
            >
              <div class="action-bar__summary-product-image">
                <LoadingSkeleton
                  class="lighting-advisor-accessories__loading-image"
                />
              </div>
              <div class="action-bar__summary-product-details">
                <LoadingSkeleton
                  class="lighting-advisor-accessories__loading-title"
                />
                <LoadingSkeleton
                  class="lighting-advisor-accessories__loading-meta"
                />
              </div>
            </div>
          </div>
        </SectionBox>

        <SectionBox class="lighting-advisor-accessories__box">
          <div class="lighting-advisor-accessories__box-content">
            <div class="action-bar__summary-cart">
              <LoadingSkeleton
                class="lighting-advisor-accessories__loading-subtotal"
              />
              <LoadingSkeleton
                class="lighting-advisor-accessories__loading-button"
              />
              <LoadingSkeleton
                class="lighting-advisor-accessories__loading-button"
              />
            </div>
          </div>
        </SectionBox>
      </div>

      <AlertBox v-else-if="loadFailed" icon="attention" :style="'warning'">
        {{
          $textDictionary['lightingAdvisor.accessories.loadError'] ??
          'The compatible accessories could not be loaded.'
        }}
      </AlertBox>

      <template v-else>
        <div
          class="action-bar__summary lighting-advisor-accessories__summary"
          data-testid="lighting-advisor-accessory-bundle"
        >
          <SectionBox class="lighting-advisor-accessories__box">
            <div class="lighting-advisor-accessories__box-content">
              <div
                v-if="selectedLightDecorated"
                class="action-bar__summary-product"
                data-testid="lighting-advisor-selected-light"
              >
                <div class="action-bar__summary-product-image">
                  <Media
                    v-if="selectedLightImage"
                    :media="selectedLightImage"
                    :cols="[3, 3, 6]"
                    :lazyload="true"
                  />
                </div>
                <div class="action-bar__summary-product-details">
                  <h4 class="action-bar__summary-product-title">
                    <span>{{ selectedLightDecorated.family }}</span>
                    <span>{{ selectedLightDecorated.modelName }}</span>
                  </h4>
                  <div class="action-bar__summary-product-price-amount">
                    <div class="action-bar__summary-product-meta">
                      <AmountInput
                        :model-value="1"
                        readonly
                        :label="
                          $textDictionary['pdp.amount.label'] ?? 'Quantity'
                        "
                      />
                    </div>
                    <div
                      v-if="selectedLightDecorated.priceText"
                      class="action-bar__summary-product-price"
                    >
                      <PriceElement
                        :current-price="selectedLightDecorated.priceText"
                        alignment="left"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <template v-if="selectedCards.length">
                <h3 class="lighting-advisor-accessories__recommendation-title">
                  {{
                    $textDictionary[
                      'lightingAdvisor.accessories.recommendationTitle'
                    ] ?? 'Our recommendation:'
                  }}
                  <DictionaryInfoIcon
                    dictionary-key="lightingAdvisor.accessories.recommendationInfo"
                    :fallback="recommendationInfoFallback"
                    tooltip-placement="top"
                  />
                </h3>

                <div
                  v-for="card in selectedCards"
                  :key="card.groupId"
                  class="action-bar__summary-product"
                  :data-testid="`lighting-advisor-accessory-${card.groupId}`"
                >
                  <div class="action-bar__summary-product-image">
                    <Media
                      v-if="accessoryImages[card.groupId]"
                      :media="accessoryImages[card.groupId]"
                      :cols="[3, 3, 6]"
                      :lazyload="true"
                    />
                  </div>
                  <div class="action-bar__summary-product-details">
                    <h4 class="action-bar__summary-product-title">
                      <span>{{ card.displayName }}</span>
                    </h4>
                    <SelectionPills
                      v-if="
                        hasLengthVariants(card) &&
                        lengthEditGroupId !== card.groupId
                      "
                      class="lighting-advisor-accessories__length-pill"
                      :items="lengthSelectionPills(card)"
                      :aria-label="lengthLabelText"
                      :data-testid="`lighting-advisor-accessory-length-${card.groupId}`"
                      @action-click="openLengthEdit(card.groupId)"
                    />
                    <div
                      v-else-if="hasLengthVariants(card)"
                      class="lighting-advisor-accessories__length-options"
                      role="group"
                      :aria-label="lengthLabelText"
                      :data-testid="`lighting-advisor-accessory-length-options-${card.groupId}`"
                    >
                      <Button
                        v-for="variant in alternativeLengthVariants(card)"
                        :key="variant.sku"
                        type="button"
                        style-type="secondary"
                        size="small"
                        :disabled="submitting"
                        :title="lengthVariantButtonLabel(variant)"
                        :aria-label="lengthVariantButtonLabel(variant)"
                        @click="selectLength(card.groupId, variant.sku)"
                      >
                        <span>{{ lengthVariantText(variant) }}</span>
                        <span
                          v-if="lengthVariantPrice(variant)"
                          class="lighting-advisor-accessories__length-price"
                        >
                          {{ lengthVariantPrice(variant) }}
                        </span>
                      </Button>
                    </div>
                    <div class="action-bar__summary-product-price-amount">
                      <div class="action-bar__summary-product-meta">
                        <AmountInput
                          :model-value="quantityFor(card.groupId)"
                          :min="1"
                          :max="99"
                          :disabled="submitting"
                          :label="
                            $textDictionary['pdp.amount.label'] ?? 'Quantity'
                          "
                          @update:model-value="
                            updateQuantity(card.groupId, $event)
                          "
                        />
                      </div>
                      <div class="action-bar__summary-product-price">
                        <PriceElement
                          v-if="priceTextFor(card)"
                          :current-price="priceTextFor(card)"
                          alignment="left"
                        />
                      </div>
                      <Button
                        type="button"
                        style-type="secondary"
                        size="small"
                        icon-type="icon-only"
                        icon-name="bin"
                        :disabled="submitting"
                        :aria-label="removeLabel(card.displayName)"
                        :title="removeLabel(card.displayName)"
                        @click="toggleGroup(card.groupId)"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </SectionBox>

          <SectionBox class="lighting-advisor-accessories__box">
            <div class="lighting-advisor-accessories__box-content">
              <div class="action-bar__summary-cart">
                <div
                  v-if="bundleSubtotal"
                  class="action-bar__summary-cart-subtotal"
                >
                  <span class="action-bar__summary-cart-subtotal-label">
                    {{ $textDictionary['cart.label.subtotal'] ?? 'Subtotal' }}
                  </span>
                  <span class="action-bar__summary-cart-subtotal-value">
                    {{ bundleSubtotal }}
                  </span>
                </div>
                <div class="action-bar__summary-cart-actions">
                  <Button
                    v-if="showAddToCartButton"
                    type="button"
                    style-type="attraction"
                    size="medium"
                    :expand="true"
                    :active-state="addToCartState"
                    :states="buttonStates"
                    :disabled="submitting"
                    data-testid="lighting-advisor-bundle-add-to-cart"
                    @click="submitBundle(cartType)"
                    @update:active-state="addToCartState = $event"
                  >
                    {{
                      $textDictionary['pdp.addToCart.label'] ?? 'Add to cart'
                    }}
                  </Button>
                  <Button
                    v-if="showRequestQuoteButton"
                    type="button"
                    style-type="attraction-secondary"
                    size="medium"
                    :expand="true"
                    :active-state="requestQuoteState"
                    :states="buttonStates"
                    :disabled="submitting"
                    data-testid="lighting-advisor-bundle-request-quote"
                    @click="submitBundle(CartTypes.REQUEST_CART)"
                    @update:active-state="requestQuoteState = $event"
                  >
                    {{
                      $textDictionary['pdp.requestButton.label'] ??
                      'Request non-binding quote'
                    }}
                  </Button>
                </div>
                <SecurePaymentInfo />
              </div>
            </div>
          </SectionBox>
        </div>

        <AlertBox
          v-for="warning in warningMessages"
          :key="warning"
          icon="attention"
          :style="'warning'"
        >
          {{ warning }}
        </AlertBox>

        <ActionBarCrossSellSection
          :cards="furtherCards"
          :title="furtherProductsTitle"
          subtitle=""
          :busy="submitting"
          overlay-append-to="self"
          test-id="lighting-advisor-accessory-more"
          card-test-id-prefix="lighting-advisor-accessory"
          @update:selected-sku="updateSelectedSku"
        >
          <template #action="{ groupId, displayName, disabled }">
            <Button
              type="button"
              style-type="attraction"
              size="small"
              icon-type="icon-only"
              icon-name="add-shopping-cart"
              :disabled="disabled"
              :aria-label="addLabel(displayName)"
              :title="addLabel(displayName)"
              @click="toggleGroup(groupId)"
            />
          </template>
        </ActionBarCrossSellSection>
      </template>
    </div>
  </ModalModule>
</template>

<script setup lang="ts">
import type {
  SelectionPillItem,
  BtnStateType
} from '@hypercodestudio/basler-components';

import {
  AlertBox,
  AmountInput,
  Button,
  LoadingSkeleton,
  Media,
  ModalModule,
  PriceElement,
  SectionBox,
  SelectionPills
} from '@hypercodestudio/basler-components';

import DictionaryInfoIcon from '~/components/global/DictionaryInfoIcon.vue';
import SecurePaymentInfo from '~/components/shop/SecurePaymentInfo.vue';
import ActionBarCrossSellSection from '~/components/shop/ProductDetailActionBar/ActionBarCrossSellSection.vue';
import { useLightingAdvisorAnalytics } from '~/composables/useLightingAdvisorAnalytics';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { CartTypes } from '~/stores/cartStore/model';
import type { LightingAnswers } from '~/types/lightingAdvisor';
import {
  accessoryPriceText,
  isDisplayableAccessoryPrice
} from '~/utils/lightingAdvisor/accessoryPriceText';
import { buildLightingAdvisorAccessoryCards } from '~/utils/lightingAdvisor/buildAccessoryCards';
import { resolveLightingAdvisorAccessoryConfiguration } from '~/utils/lightingAdvisor/accessoryConstants';
import type {
  LightingAdvisorAccessoryRecommendations,
  LightingAdvisorDecoration
} from '~/utils/lightingAdvisor/catalogTypes';
import type { LightingAdvisorAccessoryConfiguration } from '~/utils/lightingAdvisor/configurationSchema';
import { resolveLightingAdvisorWarningMessage } from '~/utils/lightingAdvisor/warningMessages';
import {
  fetchProductsWithParentVariantsBySkus,
  indexProductsBySku,
  type ActionBarCrossSellCardModel
} from '~/utils/shop/buildActionBarCrossSellCards';
import { flattenProductAttrsForDecoratedBuild } from '~/utils/shop/buildActionBarCrossSellCandidates';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { decodeHtmlEntities } from '~/utils/richtext/decodeHtmlEntities';
import { formatMoney } from '~/utils/shop/formatMoney';
import { resolveProductImageUrlFromJson } from '~/utils/shop/resolveProductImageUrlFromJson';
import { sortActionBarCrossSellVariantsByLength } from '~/utils/shop/sortActionBarCrossSellVariantsByLength';
import { generateImageObject } from '~/utils/generateImageObject';
import type { ActionBarCrossSellVariant } from '~/utils/shop/selectActionBarCrossSellProduct';

interface Props {
  modelValue: boolean;
  lightSku: string | null;
  answers: LightingAnswers;
  decorations?: LightingAdvisorDecoration[];
  accessoryConfiguration?: LightingAdvisorAccessoryConfiguration;
}

const props = withDefaults(defineProps<Props>(), {
  decorations: () => [],
  accessoryConfiguration: () => ({})
});
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const { $globalPageSettings, $activeLocale, $shopStore, $textDictionary } =
  useNuxtApp();
const logger = useLogger();
const cartStore = useCartStore();
const { loggedIn } = useOidcAuth();
const analytics = useLightingAdvisorAnalytics();
const resolvedAccessoryConfiguration = computed(() =>
  resolveLightingAdvisorAccessoryConfiguration(props.accessoryConfiguration)
);

const loading = ref(false);
const loadFailed = ref(false);
const lightName = ref('');
const selectedLightDecorated = ref<DecoratedProduct | null>(null);
const lightPriceValue = ref<number | null>(null);
const lightCurrency = ref<string | null>(null);
const cards = ref<ActionBarCrossSellCardModel[]>([]);
const selectedGroupIds = ref<Set<string>>(new Set());
const recommendedGroupIds = ref<Set<string>>(new Set());
const quantitiesByGroupId = ref<Record<string, number>>({});
const lengthEditGroupId = ref<string | null>(null);
const warnings = ref<string[]>([]);
const showAddToCartButton = ref(false);
const showRequestQuoteButton = ref(false);
const addToCartState = ref<BtnStateType | undefined>('initial');
const requestQuoteState = ref<BtnStateType | undefined>('initial');

const open = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    if (!value && props.modelValue) {
      analytics.trackEvent(
        'back_btn',
        `back_Overview Page_${lightName.value || props.lightSku || ''}`
      );
    }
    emit('update:modelValue', value);
  }
});

const selectedCards = computed(() =>
  cards.value.filter((card) => selectedGroupIds.value.has(card.groupId))
);
const furtherCards = computed(() =>
  cards.value.filter((card) => !selectedGroupIds.value.has(card.groupId))
);
const selectedSkus = computed(() =>
  selectedCards.value.map((card) => card.selectedVariantSku)
);

const lengthLabelText = computed(
  () => $textDictionary.value['actionBar.crossSell.lengthLabel'] ?? 'Length'
);

const changeLengthText = computed(
  () => $textDictionary.value['actionBar.crossSell.changeLength'] ?? 'Change'
);

const selectedLightImage = computed(() =>
  selectedLightDecorated.value?.getProductImage(
    {
      sizes: '200px',
      widths: [400, 600, 800, 1000],
      aspectRatio: [4, 3]
    },
    $globalPageSettings.value?.shopConfig?.defaultProductImage ?? undefined
  )
);

const accessoryImages = computed(() =>
  Object.fromEntries(
    cards.value.map((card) => {
      const selectedVariant = card.variants.find(
        (variant) => variant.sku === card.selectedVariantSku
      );
      const rawImage = selectedVariant?.imageJson ?? card.imageJson;
      const imageUrl = rawImage
        ? resolveProductImageUrlFromJson(rawImage)
        : card.imageUrl;
      return [
        card.groupId,
        generateImageObject(
          imageUrl
            ? {
                url: imageUrl,
                altTag: decodeHtmlEntities(card.displayName)
              }
            : undefined,
          {
            sizes: '200px',
            widths: [400, 600, 800, 1000],
            aspectRatio: [4, 3]
          }
        )
      ];
    })
  )
);

const priceOnRequestLabel = computed(
  () => $textDictionary.value['pdp.priceOnRequest.label'] ?? 'Price on request'
);

const bundleSubtotal = computed(() => {
  let value = lightPriceValue.value ?? 0;
  let currency = lightCurrency.value;
  for (const card of selectedCards.value) {
    const variant = selectedVariantFor(card);
    if (isDisplayableAccessoryPrice(variant?.priceValue)) {
      value += variant.priceValue * quantityFor(card.groupId);
      currency ??= variant.currency ?? null;
    }
  }
  if (!currency || value <= 0) {
    return '';
  }
  return formatMoney(
    { value, currency: currency as never },
    $activeLocale.value.webapp
  );
});

const recommendationInfoFallback =
  'For the requirements you specified, we have also selected the following components that are necessary for the use you described. Please note that this is an initial recommendation. You can of course simply deselect the components. However, we recommend testing the selection once.';
const submitting = computed(
  () =>
    addToCartState.value === 'loading' || requestQuoteState.value === 'loading'
);

const furtherProductsTitle = computed(() => {
  const template =
    $textDictionary.value['lightingAdvisor.accessories.furtherTitle'] ??
    '{count} more compatible accessory products';
  return template.replace('{count}', String(furtherCards.value.length));
});

const warningMessages = computed(() =>
  [
    ...new Set([
      ...warnings.value,
      ...props.decorations
        .filter(
          (decoration) =>
            !decoration.questionId &&
            (!decoration.ordernumber ||
              decoration.ordernumber === props.lightSku)
        )
        .map((decoration) => decoration.identifier)
    ])
  ]
    .filter(
      (identifier) =>
        (identifier !== 'warning_light_add_diffuser_foil' ||
          !selectedCards.value.some((card) =>
            card.displayName.toLowerCase().includes('diffuser')
          )) &&
        (identifier !== 'warning_light_country_specific_iec_plug' ||
          selectedSkus.value.every(
            (sku) =>
              !resolvedAccessoryConfiguration.value.powerCordSkus.includes(sku)
          ))
    )
    .map((identifier) =>
      resolveLightingAdvisorWarningMessage(
        identifier,
        ($textDictionary.value ?? {}) as Record<string, string | undefined>
      )
    )
);

const buttonStates = computed(() => ({
  loading: {
    text:
      $textDictionary.value['checkout.common.loadingStateButton.label'] ??
      'Processing',
    ariaText:
      $textDictionary.value['checkout.common.loadingStateButton.ariaText'] ??
      'Processing',
    resetDelay: -1
  },
  success: {
    text: $textDictionary.value['pdp.addToCart.buttonState.success'] ?? 'Done',
    ariaText:
      $textDictionary.value['pdp.addToCart.buttonState.success'] ?? 'Done',
    resetDelay: 2000
  },
  error: {
    text:
      $textDictionary.value['checkout.common.errorStateButton.label'] ??
      'Error',
    ariaText:
      $textDictionary.value['checkout.common.errorStateButton.ariaText'] ??
      'Error',
    resetDelay: 2000
  }
}));

const cartType = computed(() =>
  loggedIn.value ? CartTypes.CUSTOMER_CART : CartTypes.CART
);

function removeLabel(displayName: string): string {
  return `${
    $textDictionary.value['lightingAdvisor.accessories.remove'] ?? 'Remove'
  }: ${displayName}`;
}

function addLabel(displayName: string): string {
  return `${
    $textDictionary.value['lightingAdvisor.accessories.add'] ?? 'Select'
  }: ${displayName}`;
}

function selectedVariantFor(
  card: ActionBarCrossSellCardModel
): ActionBarCrossSellVariant | undefined {
  return card.variants.find(
    (variant) => variant.sku === card.selectedVariantSku
  );
}

function quantityFor(groupId: string): number {
  return quantitiesByGroupId.value[groupId] ?? 1;
}

function updateQuantity(groupId: string, value: number | undefined): void {
  const quantity = Math.min(99, Math.max(1, Number(value) || 1));
  quantitiesByGroupId.value = {
    ...quantitiesByGroupId.value,
    [groupId]: quantity
  };
  analytics.trackEvent('accessory_quantity_change', { groupId, quantity });
}

function hasLengthVariants(card: ActionBarCrossSellCardModel): boolean {
  return card.variants.length > 1;
}

function lengthVariantText(variant: ActionBarCrossSellVariant): string {
  return decodeHtmlEntities(variant.lengthLabel || variant.name || variant.sku);
}

function lengthVariantPrice(variant: ActionBarCrossSellVariant): string {
  return accessoryPriceText({
    priceValue: variant.priceValue,
    currency: variant.currency,
    locale: $activeLocale.value.webapp,
    onRequestLabel: priceOnRequestLabel.value
  });
}

function lengthVariantButtonLabel(variant: ActionBarCrossSellVariant): string {
  return [lengthVariantText(variant), lengthVariantPrice(variant)]
    .filter(Boolean)
    .join(' ');
}

function lengthSelectionPills(
  card: ActionBarCrossSellCardModel
): SelectionPillItem[] {
  const current = selectedVariantFor(card);
  return [
    {
      id: `lighting-advisor-accessory-length-${card.groupId}`,
      label: `${lengthLabelText.value}: ${
        current ? lengthVariantText(current) : card.selectedVariantSku
      }`,
      linkText: changeLengthText.value,
      isClickable: !submitting.value
    }
  ];
}

function alternativeLengthVariants(
  card: ActionBarCrossSellCardModel
): ActionBarCrossSellVariant[] {
  return sortActionBarCrossSellVariantsByLength(
    card.variants.filter((variant) => variant.sku !== card.selectedVariantSku)
  );
}

function openLengthEdit(groupId: string): void {
  if (submitting.value) {
    return;
  }
  lengthEditGroupId.value = groupId;
}

function selectLength(groupId: string, sku: string): void {
  lengthEditGroupId.value = null;
  updateSelectedSku(sku, groupId, 0);
}

function priceTextFor(card: ActionBarCrossSellCardModel): string {
  const variant = selectedVariantFor(card);
  return accessoryPriceText({
    priceValue: variant?.priceValue,
    currency: variant?.currency,
    locale: $activeLocale.value.webapp,
    onRequestLabel: priceOnRequestLabel.value
  });
}

function toggleGroup(groupId: string): void {
  const next = new Set(selectedGroupIds.value);
  const selected = next.has(groupId);
  if (selected) {
    next.delete(groupId);
  } else {
    next.add(groupId);
    if (!quantitiesByGroupId.value[groupId]) {
      updateQuantity(groupId, 1);
    }
  }
  selectedGroupIds.value = next;
  if (lengthEditGroupId.value === groupId) {
    lengthEditGroupId.value = null;
  }
  const card = cards.value.find((item) => item.groupId === groupId);
  if (selected && recommendedGroupIds.value.has(groupId)) {
    analytics.trackEvent(
      'deselect',
      `${card?.displayName ?? groupId}, accessory`
    );
  }
  analytics.trackEvent(selected ? 'accessory_remove' : 'accessory_add', {
    sku: card?.selectedVariantSku ?? card?.productSku ?? groupId
  });
}

function updateSelectedSku(
  sku: string,
  groupId: string,
  _position: number
): void {
  cards.value = cards.value.map((card) =>
    card.groupId === groupId ? { ...card, selectedVariantSku: sku } : card
  );
  analytics.trackEvent('accessory_variant_change', { sku, groupId });
}

async function loadAccessories(): Promise<void> {
  if (!props.lightSku) {
    return;
  }
  loading.value = true;
  loadFailed.value = false;
  showAddToCartButton.value = false;
  showRequestQuoteButton.value = false;
  selectedLightDecorated.value = null;
  lightPriceValue.value = null;
  lightCurrency.value = null;
  lengthEditGroupId.value = null;
  addToCartState.value = 'initial';
  requestQuoteState.value = 'initial';
  try {
    const recommendation =
      await $fetch<LightingAdvisorAccessoryRecommendations>(
        '/api/tools/lighting/accessories',
        {
          query: {
            locale: $activeLocale.value.webapp,
            store: $shopStore.value,
            sku: props.lightSku,
            accessoryConfiguration: JSON.stringify(
              props.accessoryConfiguration ?? {}
            ),
            ...props.answers
          }
        }
      );
    const productSkus = [props.lightSku, ...recommendation.availableSkus];
    const products = await fetchProductsWithParentVariantsBySkus(
      productSkus,
      $shopStore.value,
      $activeLocale.value.webapp
    );
    const productsBySku = indexProductsBySku(products);
    const lightProduct = productsBySku.get(props.lightSku);
    lightName.value = lightProduct?.name ?? props.lightSku;
    if (lightProduct) {
      selectedLightDecorated.value = DecoratedProduct.build(
        flattenProductAttrsForDecoratedBuild(
          lightProduct
        ) as unknown as Parameters<typeof DecoratedProduct.build>[0],
        $textDictionary.value ?? {},
        $activeLocale.value.webapp
      );
      // Mirror PDP: price must be visible and product in stock (e.g. India has no prices → no add-to-cart)
      showAddToCartButton.value = selectedLightDecorated.value.isBuyable();
      showRequestQuoteButton.value =
        selectedLightDecorated.value.showRequestCartButton;
      lightPriceValue.value = selectedLightDecorated.value.showPrice
        ? (lightProduct.price_range?.minimum_price?.final_price?.value ?? null)
        : null;
      lightCurrency.value = selectedLightDecorated.value.showPrice
        ? (lightProduct.price_range?.minimum_price?.final_price?.currency ??
          null)
        : null;
    }
    cards.value = buildLightingAdvisorAccessoryCards(
      recommendation.availableSkus,
      productsBySku,
      $activeLocale.value.webapp,
      priceOnRequestLabel.value
    );
    recommendedGroupIds.value = new Set(
      cards.value
        .filter((card) =>
          card.variants.some((variant) =>
            recommendation.selectedSkus.includes(variant.sku)
          )
        )
        .map((card) => card.groupId)
    );
    selectedGroupIds.value = new Set(recommendedGroupIds.value);
    quantitiesByGroupId.value = Object.fromEntries(
      cards.value.map((card) => [card.groupId, 1])
    );
    cards.value = cards.value.map((card) => {
      const recommendedSku = card.variants.find((variant) =>
        recommendation.selectedSkus.includes(variant.sku)
      )?.sku;
      return recommendedSku
        ? { ...card, selectedVariantSku: recommendedSku }
        : card;
    });
    warnings.value = recommendation.warningIdentifiers;
  } catch (error) {
    logger.error('Lighting advisor accessories failed', error);
    loadFailed.value = true;
    cards.value = [];
    selectedLightDecorated.value = null;
    lightPriceValue.value = null;
    lightCurrency.value = null;
    selectedGroupIds.value = new Set();
    recommendedGroupIds.value = new Set();
    quantitiesByGroupId.value = {};
    showAddToCartButton.value = false;
    showRequestQuoteButton.value = false;
  } finally {
    loading.value = false;
  }
}

async function submitBundle(
  targetCart:
    | typeof CartTypes.CART
    | typeof CartTypes.CUSTOMER_CART
    | typeof CartTypes.REQUEST_CART
): Promise<void> {
  if (!props.lightSku) {
    return;
  }
  const state =
    targetCart === CartTypes.REQUEST_CART ? requestQuoteState : addToCartState;
  state.value = 'loading';
  const accessoryItems = selectedCards.value.map((card) => ({
    sku: card.selectedVariantSku,
    quantity: quantityFor(card.groupId)
  }));
  try {
    await cartStore.addProducts(
      [{ sku: props.lightSku, quantity: 1 }, ...accessoryItems],
      targetCart,
      {
        source: 'LightingAdvisor',
        productName: lightName.value || props.lightSku
      }
    );
    if (!cartStore.lastAction?.success) {
      throw new Error('The lighting bundle could not be added to the cart');
    }
    state.value = 'success';
    const isRequestCart = targetCart === CartTypes.REQUEST_CART;
    analytics.trackEvent(
      isRequestCart ? 'request_quote' : 'add_to_cart',
      `${lightName.value || props.lightSku} (${props.lightSku})`
    );
    analytics.trackSelectionDetails({
      selectedItem: lightName.value || props.lightSku,
      selectedItemId: props.lightSku,
      selectedAccessorySkus: accessoryItems.map((item) => item.sku),
      type: isRequestCart ? 'req' : 'sp',
      cartId: cartStore.carts[targetCart]?.id
    });
  } catch (error) {
    logger.error('Lighting advisor bundle submit failed', error);
    state.value = 'error';
  }
}

watch(
  () => [props.modelValue, props.lightSku] as const,
  ([isOpen, lightSku], previous) => {
    if (isOpen && lightSku && (!previous?.[0] || previous[1] !== lightSku)) {
      void loadAccessories();
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.lighting-advisor-accessories {
  display: grid;
  gap: 1rem;
}

.lighting-advisor-accessories__intro {
  margin: 0;
}

/**
 * SectionBox replaces `action-bar__summary-box` as the card, so the flex row of
 * `action-bar__summary` needs its columns declared here.
 */
.lighting-advisor-accessories__box {
  flex: 1 1 0;
  min-width: 0;
}

.lighting-advisor-accessories__box-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.5rem;
  width: 100%;
}

.lighting-advisor-accessories__recommendation-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  margin: 0;
  text-align: left;
}

.lighting-advisor-accessories__loading-image {
  width: 100%;
  aspect-ratio: 4 / 3;
}

.lighting-advisor-accessories__loading-title {
  width: 60%;
  height: 1.5rem;
}

.lighting-advisor-accessories__loading-meta {
  width: 100%;
  height: 2.25rem;
  margin-block-start: 0.75rem;
}

.lighting-advisor-accessories__loading-subtotal {
  width: 100%;
  height: 1.5rem;
}

.lighting-advisor-accessories__loading-button {
  width: 100%;
  height: 2.75rem;
}

.lighting-advisor-accessories__summary {
  width: 100%;
}

.lighting-advisor-accessories__length-pill {
  margin: 0.25rem 0 0.5rem;
  padding-block-end: 0;
}

.lighting-advisor-accessories__length-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-block: 0.5rem;
}

.lighting-advisor-accessories__length-price {
  margin-inline-start: 0.75rem;
  font-weight: 400;
}
</style>
